import { Box, BoxProps, Flex, Text } from '@chakra-ui/react'
import { forwardRef, ReactNode } from 'react'

import { Icon } from '../Icon'
import { SECTION_CARD_SHELL } from '../SectionCard'

export type EmptyStateVariant = 'inline' | 'panel'
export type EmptyStateTone = 'neutral' | 'error'

/**
 * Token para a forma do espaço, não medida do Chakra — a mesma ideia do `size` do `FormModal` e
 * do `LoadingBlock`.
 *
 * `inline` (padrão) é o vazio *dentro* de um card ou de uma seção que já tem moldura: só o ícone
 * e a frase, sem borda nenhuma. `panel` é o bloco que fica no lugar do conteúdo inteiro da tela,
 * e por isso desenha a própria moldura tracejada.
 */
const VARIANTS: Record<EmptyStateVariant, BoxProps> = {
  inline: { py: 10 },
  // A casca é a do `SectionCard`, com a borda tracejada por cima: o painel vazio é um card sem
  // conteúdo, e herdar o raio daqui foi o que tirou os três `borderRadius="14px"` que as cópias
  // do `ArtworkEvaluationPage` e do `SurveyReportPage` carregavam.
  panel: { ...SECTION_CARD_SHELL, borderStyle: 'dashed', borderColor: 'gray.300', py: 16, px: 6 }
}

const TONES: Record<EmptyStateTone, { bg: string; color: string }> = {
  neutral: { bg: 'gray.100', color: 'gray.500' },
  error: { bg: 'red.50', color: 'red.500' }
}

export interface IEmptyStateProps extends Omit<BoxProps, 'title'> {
  /** Classe do LineIcons (`lni lni-users`) ou um nó pronto, para os ícones do react-icons. */
  icon?: ReactNode
  /** A frase principal: o que não há. Segue a convenção do `TableEmptyRow` — sem ponto final. */
  title: ReactNode
  /** Linha de apoio, para dizer o que fazer a seguir ("Ajuste os filtros..."). */
  description?: ReactNode
  /** Botão de saída — "Tentar novamente", "Cadastrar o primeiro". */
  action?: ReactNode
  /** Forma do espaço. `inline` (padrão) dentro de um card; `panel` no lugar da tela. */
  variant?: EmptyStateVariant
  /** `error` pinta o ladrilho do ícone de vermelho, para o vazio que é uma falha. */
  tone?: EmptyStateTone
}

/**
 * O "nada aqui" de uma grade, de uma galeria ou de uma tela — o irmão do `<TableEmptyRow>` para
 * o conteúdo que não é uma tabela.
 *
 * Eram quatro desenhos do mesmo bloco: o `EmptyState` local do `EventAdminPage` (4 usos, ícone
 * num círculo de 48px), os três blocos tracejados do `ArtworkEvaluationPage` e o do
 * `SurveyReportPage`. O eixo dos cards deixou os tracejados de fora de propósito, dizendo que
 * eram assunto do eixo de estado vazio; é este.
 *
 * A página declara o que falta; a moldura, o respiro e a cor moram aqui. O ref é encaminhado
 * para o `motion(EmptyState)` do `ArtworkEvaluation`, pelo mesmo motivo que o `SectionCard` o
 * encaminha.
 */
const EmptyState = forwardRef<HTMLDivElement, IEmptyStateProps>(function EmptyState(
  { icon, title, description, action, variant = 'inline', tone = 'neutral', ...rest },
  ref
) {
  const isPanel = variant === 'panel'
  const { bg, color } = TONES[tone]

  return (
    <Box ref={ref} textAlign="center" {...VARIANTS[variant]} {...rest}>
      {icon && (
        <Flex
          mx="auto"
          mb={isPanel ? 4 : 2}
          align="center"
          justify="center"
          w={isPanel ? '56px' : '48px'}
          h={isPanel ? '56px' : '48px'}
          borderRadius={isPanel ? 'xl' : 'full'}
          bg={isPanel ? bg : 'gray.50'}
          color={isPanel ? color : 'gray.400'}
          fontSize={isPanel ? '24px' : '20px'}
        >
          {typeof icon === 'string' ? <Icon name={icon} /> : icon}
        </Flex>
      )}

      <Text
        fontSize={isPanel ? 'lg' : 'sm'}
        fontWeight={isPanel ? '600' : 'normal'}
        color={isPanel ? 'gray.900' : 'gray.400'}
        mb={description ? 1 : 0}
      >
        {title}
      </Text>

      {description && (
        <Text variant="caption" maxW="400px" mx="auto" mb={action ? 5 : 0}>
          {description}
        </Text>
      )}

      {action}
    </Box>
  )
})

export default EmptyState
